'use client'

import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import {
  ArrowRightLeft,
  LogOut,
  User,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import {
  MouseEvent,
  useCallback,
} from 'react'
import toast from 'react-hot-toast'

import Copy from '@/components/icons/copy.svg'
import Nickname from '@/components/nickname'
import Avatar from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useLogin from '@/hooks/use-login'
import useLogout from '@/hooks/use-logout'
import useReadMineUserQueryWhenLogin from '@/hooks/use-read-mine-user-query-when-login'
import { useSwitchAddress } from '@/hooks/use-switch-address'
import {
  formatAddress,
  formatPrice,
} from '@/lib/formatter'
import { cn } from '@/lib/utils'
import { useReadAllWalletsQuery } from '@/redux/wallet'

export interface AccountDropdownProps {
  isHome?: boolean
}

export default function AccountDropdown({ isHome }: AccountDropdownProps) {
  const { data: mineUser } = useReadMineUserQueryWhenLogin()
  const { data: allWallets } = useReadAllWalletsQuery()
  const wallet = allWallets?.find(({ id }) => id === mineUser?.wallets[0].walletId)
  const walletLogo = wallet?.logo
  const address = mineUser?.wallets[0].address

  const onLogin = useLogin()
  const onCopyAddress = useCallback(async (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (address) {
      await navigator.clipboard.writeText(address)
      toast.success('Address copied')
    }
  }, [address])

  const switchAddress = useSwitchAddress()

  const onSwitchAddress = useCallback(async (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    switchAddress(address)()
  }, [address, switchAddress])

  const [onLogout] = useLogout()

  return mineUser ? (
    <Popover>
      <PopoverTrigger>
        <div className="flex gap-2 items-center cursor-pointer">
          <Avatar size="sm" src={mineUser.avatar} />
          <Nickname user={mineUser} className="text-sm" />
        </div>
      </PopoverTrigger>
      <PopoverContent sideOffset={8} className="w-80 border border-white/8 rounded-2xl">
        <div className="flex gap-3 p-3">
          <Link href={`/account/${mineUser.id}`}>
            <Avatar src={mineUser.avatar} />
          </Link>
          <div className="py-1">
            <Nickname user={mineUser} />
            <div className="text-sm text-white/50">Balance: {formatPrice()(mineUser.stats.balance)}</div>
          </div>
        </div>
        <div className="text-xs text-secondary-foreground px-3 py-2">Current Wallet</div>
        <div className="flex flex-col gap-3">
          <div className="text-xs px-3 py-2 flex hover:bg-white/5 rounded-lg justify-between">
            <div className="flex items-center">
              {walletLogo &&
                <Image className="mr-2" src={walletLogo} width={16} height={16} alt="" />}
              <div className="text-xs mr-1">{formatAddress()(address)}</div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="p-1 size-5 hover:bg-white/8 rounded-sm cursor-pointer" onClick={onCopyAddress}>
                      <Copy className="size-full fill-tertiary-foreground" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" withArrow className="bg-background-tertiary">
                    <p>Copy</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <VisuallyHidden>
              <div
                className="p-1 size-5 hover:bg-white/8 rounded-sm"
                onClick={onSwitchAddress}
              >
                <ArrowRightLeft className="size-full" />
              </div>
            </VisuallyHidden>
          </div>
          <Separator />
          <Link
            href="/account-settings"
            className="text-xs px-3 py-2 flex hover:bg-white/5 rounded-lg items-center gap-2"
          >
            <User className="size-4" />
            Account Settings
          </Link>
          <div
            className="text-xs px-3 py-2 flex hover:bg-white/5 rounded-lg cursor-pointer items-center gap-2"
            onClick={onLogout}
          >
            <LogOut className="size-4" />
            Logout
          </div>
        </div>
      </PopoverContent>
    </Popover>
  ) : (
    <Button
      variant="primary"
      className={cn(
        'relative from-gradient-blue to-gradient-purple font-bold before:rounded-full before:absolute before:inset-0 before:bg-white/0 hover:before:bg-white/8',
        isHome ? 'bg-linear-[308deg] from-[33.35%] to-[81.2%]' : 'bg-linear-to-r',
      )}
      onClick={onLogin}
    >
      Connect Wallet
    </Button>
  )
}
